import React, { useState, useEffect } from 'react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { fetchChangelog, dismissChangelog } from '../services/api';
import {
  X, Sparkles, Trophy, Crown, ShoppingBag, Shield, FlaskConical,
  ClipboardList, BookOpen, Map, Megaphone, Gift, ArrowRight
} from 'lucide-react';

const ICON_MAP = {
  Sparkles, Trophy, Crown, ShoppingBag, Shield, FlaskConical,
  ClipboardList, BookOpen, Map, Megaphone, Gift,
};

export default function WhatsNewBanner() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user || !user.tour_completed) return;
    fetchChangelog()
      .then(d => {
        if (d.has_new) {
          setData(d);
          setShow(true);
        }
      })
      .catch(() => {});
  }, [user]);

  const handleDismiss = async () => {
    setShow(false);
    try {
      await dismissChangelog();
      setUser(prev => prev ? { ...prev, changelog_version_seen: data?.current_version } : prev);
    } catch {}
  };

  if (!show || !data) return null;

  const latestEntry = data.unseen_entries[0];

  // Collapsed banner
  if (!expanded) {
    return (
      <div data-testid="whats-new-banner" style={{
        margin: '0 0 16px', borderRadius: 16, overflow: 'hidden',
        background: 'linear-gradient(135deg, #6B8E23 0%, #4a6e18 50%, #3d5c14 100%)',
        boxShadow: '0 4px 20px rgba(107,142,35,0.2)',
        animation: 'fadeInUp 0.4s ease-out',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 12,
          cursor: 'pointer',
        }} onClick={() => setExpanded(true)}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Megaphone size={18} color="#FFF" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#FFF' }}>
              What's New — {latestEntry?.title}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
              {data.unseen_entries.length} update{data.unseen_entries.length > 1 ? 's' : ''} since your last visit
            </div>
          </div>
          <button data-testid="whats-new-expand" style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 14px', borderRadius: 9999,
            backgroundColor: 'rgba(255,255,255,0.2)', border: 'none',
            color: '#FFF', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            See what's new <ArrowRight size={14} />
          </button>
          <button data-testid="whats-new-dismiss-banner" onClick={e => { e.stopPropagation(); handleDismiss(); }} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: 'rgba(255,255,255,0.6)',
          }}>
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Expanded modal
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={handleDismiss} />
      <div data-testid="whats-new-modal" style={{
        position: 'relative', zIndex: 1, backgroundColor: '#FFF', borderRadius: 24,
        maxWidth: 520, width: '100%', maxHeight: '80vh', overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        animation: 'fadeInUp 0.3s ease-out',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #6B8E23, #4a6e18)',
          padding: '24px 28px 20px', position: 'relative',
        }}>
          <button data-testid="whats-new-close" onClick={handleDismiss} style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(255,255,255,0.2)', border: 'none',
            borderRadius: 8, padding: 4, cursor: 'pointer',
          }}>
            <X size={18} color="#FFF" />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Megaphone size={24} color="#FFF" />
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>What's New</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Latest updates to ThriveLonger.ai</div>
            </div>
          </div>
        </div>

        {/* Entries */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 24px' }}>
          {data.unseen_entries.map((entry, idx) => (
            <div key={entry.version} style={{ marginBottom: idx < data.unseen_entries.length - 1 ? 24 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700,
                  backgroundColor: idx === 0 ? `${COLORS.primary}15` : `${COLORS.border}40`,
                  color: idx === 0 ? COLORS.primary : COLORS.textSecondary,
                }}>
                  v{entry.version}
                </span>
                <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{entry.date}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{entry.title}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {entry.highlights.map((h, j) => {
                  const HIcon = ICON_MAP[h.icon] || Sparkles;
                  return (
                    <div key={j} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '10px 14px', borderRadius: 12,
                      backgroundColor: h.new ? `${COLORS.primary}08` : COLORS.surfaceSecondary,
                      border: h.new ? `1px solid ${COLORS.primary}20` : 'none',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        backgroundColor: h.new ? `${COLORS.primary}18` : `${COLORS.border}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <HIcon size={16} color={h.new ? COLORS.primary : COLORS.textSecondary} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.4 }}>{h.text}</span>
                        {h.new && <span style={{
                          display: 'inline-block', marginLeft: 6,
                          padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                          backgroundColor: COLORS.primary, color: '#FFF',
                        }}>NEW</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 28px 20px', borderTop: `1px solid ${COLORS.border}30` }}>
          <button data-testid="whats-new-got-it" onClick={handleDismiss} style={{
            width: '100%', padding: '14px', borderRadius: 12, border: 'none',
            backgroundColor: COLORS.primary, color: '#FFF', fontSize: 15,
            fontWeight: 700, cursor: 'pointer',
          }}>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}
